import axios from 'axios';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'easer_token';
const USER_KEY = 'easer_user';

export const getToken = () => localStorage.getItem(TOKEN_KEY) || '';
export const getStoredUser = () => {
  try { return JSON.parse(localStorage.getItem(USER_KEY) || 'null'); } catch { return null; }
};
export const storeSession = (token, user) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};
export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

// Mode dégradé Dolibarr : toute réponse serveur portant `dolibarr_unavailable: true` prévient l'app
// (lib/dolibarrStatus.js écoute cet évènement) sans créer d'import circulaire.
axios.interceptors.response.use(undefined, (error) => {
  if (error?.response?.data?.dolibarr_unavailable && typeof window !== 'undefined') {
    try { window.dispatchEvent(new CustomEvent('easer:dolibarr-error', { detail: error.response.data.detail })); } catch { /* old browsers */ }
  }
  return Promise.reject(error);
});

/** Réponse serveur signalant que Dolibarr lui-même n'a pas répondu (le réseau, lui, fonctionne). */
export const isDolibarrUnavailable = (error) => !!error?.response?.data?.dolibarr_unavailable;

export const easerGet = (path, params = {}) => axios.get(`${BACKEND_URL}${path}`, { params, headers: authHeaders() });
export const easerPost = (path, body = {}) => axios.post(`${BACKEND_URL}${path}`, body, { headers: authHeaders() });
export const easerDelete = (path) => axios.delete(`${BACKEND_URL}${path}`, { headers: authHeaders() });
export const easerPut = (path, body = {}) => axios.put(`${BACKEND_URL}${path}`, body, { headers: authHeaders() });
export const documentUrl = (relativename) =>
  `${BACKEND_URL}/api/easer/documents/download?file=${encodeURIComponent(relativename)}&token=${encodeURIComponent(getToken())}`;

/** Human-readable error message from an axios error. */
export const errorMessage = (error, fallback = 'Une erreur est survenue') => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return error?.message || fallback;
};

export const isUnauthorized = (error) => error?.response?.status === 401;

/** Best-effort geolocation (resolves to {lat,lng} or {} — never rejects). */
export const getPosition = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve({});
  const done = (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
  navigator.geolocation.getCurrentPosition(done, () => resolve({}), { enableHighAccuracy: true, timeout: 6000, maximumAge: 60000 });
});

const TIME_FMT = { hour: '2-digit', minute: '2-digit' };
export const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString('fr-FR', TIME_FMT) : '--:--');
export const fmtDate = (iso, opts = { weekday: 'long', day: 'numeric', month: 'long' }) => (iso ? new Date(iso).toLocaleDateString('fr-FR', opts) : '');
export const fmtShortDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '');
export const toISODate = (d) => {
  const z = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return z.toISOString().slice(0, 10);
};

/** 125 -> "2h05", 60 -> "1h00", 0 -> "0h00". */
export const fmtMinutes = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  return `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}`;
};

/** Secure photo URL for the Easer (mobile browsers open images without headers). */
export const reportPhotoUrl = (reportId, index) =>
  `${BACKEND_URL}/api/easer/reports/${reportId}/photos/${index}?token=${encodeURIComponent(getToken())}`;

/**
 * Downscale a picked image on the device before upload (max 1600px, JPEG) so a
 * 12 MP phone photo becomes ~300 KB. Resolves to a data URL.
 */
export const compressImage = (file, maxSide = 1600, quality = 0.85) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    resolve(canvas.toDataURL('image/jpeg', quality));
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image illisible')); };
  img.src = url;
});
